import React, { useEffect, useState } from "react";
import axios from "axios";
import { Lock, Unlock, AlertTriangle, CheckCircle2, Shield } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Widget admin per accendere/spegnere il "gate pre-lancio" senza toccare .env.
 * Se la password (SITE_ACCESS_PASSWORD) non è configurata in .env, il toggle è
 * bloccato e viene mostrato un avviso.
 */
export default function SiteAccessGateToggle() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/site-access/admin/config`, { withCredentials: true });
      setCfg(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Errore caricamento configurazione");
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (next) => {
    if (!window.confirm(
      next
        ? "Attivare il gate pre-lancio? Tutti i visitatori (tranne /admin) dovranno inserire la password per accedere."
        : "Disattivare il gate? Il sito diventerà pubblico per tutti (go-live)."
    )) return;
    setLoading(true); setErr(""); setMsg("");
    try {
      const { data } = await axios.post(`${API}/site-access/admin/toggle`, { enabled: next }, { withCredentials: true });
      setMsg(data.enabled ? "Gate ATTIVATO" : "Gate DISATTIVATO - sito live per tutti");
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setLoading(false);
      setTimeout(() => setMsg(""), 4000);
    }
  };

  if (!cfg) return null;

  const envReady = cfg.password_set_in_env;
  const isOn = cfg.enabled;

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 p-5 mb-5" data-testid="site-access-toggle">
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
          isOn ? "bg-amber-100 border border-amber-200" : "bg-emerald-100 border border-emerald-200"
        }`}>
          {isOn ? <Lock className="w-5 h-5 text-amber-700" /> : <Unlock className="w-5 h-5 text-emerald-700" />}
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-zinc-900">Gate pre-lancio sito pubblico</h3>
          <p className="text-xs text-zinc-500 mt-0.5">
            Quando attivo, i visitatori del sito pubblico devono inserire la password per accedere.
            Non influenza il pannello Admin.
          </p>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${
          isOn ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"
        }`}>
          <span className={`w-2 h-2 rounded-full ${isOn ? "bg-amber-500 animate-pulse" : "bg-emerald-500"}`} />
          {isOn ? "Gate attivo" : "Sito live"}
        </span>
      </div>

      {!envReady ? (
        <div className="flex items-start gap-2 bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 text-xs text-zinc-600">
          <AlertTriangle className="w-4 h-4 text-zinc-500 mt-0.5 flex-shrink-0" />
          <span>
            La password del gate non è configurata in <code className="bg-white px-1 rounded">SITE_ACCESS_PASSWORD</code>.
            Finché la variabile è vuota, il gate non può essere attivato e il sito è sempre pubblico.
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-2 pt-2 border-t border-zinc-100">
          <button
            onClick={() => toggle(false)}
            disabled={loading || !isOn}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
              !isOn
                ? "bg-emerald-600 text-white border-emerald-600 cursor-default"
                : "border-zinc-200 text-zinc-700 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700"
            }`}
            data-testid="gate-disable-btn"
          >
            <Unlock className="w-4 h-4" />
            {!isOn ? "Sito live" : "Disattiva gate (go-live)"}
          </button>
          <button
            onClick={() => toggle(true)}
            disabled={loading || isOn}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
              isOn
                ? "bg-amber-500 text-white border-amber-500 cursor-default"
                : "border-zinc-200 text-zinc-700 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700"
            }`}
            data-testid="gate-enable-btn"
          >
            <Lock className="w-4 h-4" />
            {isOn ? "Gate attivo" : "Riattiva gate pre-lancio"}
          </button>
        </div>
      )}

      {msg && (
        <div className="mt-3 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
          <CheckCircle2 className="w-4 h-4" /> {msg}
        </div>
      )}
      {err && (
        <div className="mt-3 flex items-center gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
          <AlertTriangle className="w-4 h-4" /> {err}
        </div>
      )}

      {cfg.last_change && (
        <p className="mt-3 text-[11px] text-zinc-400 flex items-center gap-1">
          <Shield className="w-3 h-3" />
          Ultima modifica: {new Date(cfg.last_change).toLocaleString("it-IT")} da {cfg.last_change_by}
        </p>
      )}
    </div>
  );
}
